import type { PublicKey } from "@solana/web3.js";

import * as impl from "./zkProof.impl.js";

export type DepositParams = {
  nullifier: string;
  secret: string;
  amount: string;
  commitment: string;
  nullifierHash: string;
  mint: string | null;
};

export type GenerateDepositParamsResult = {
  params: DepositParams;
  note: string;
};

export type GenerateWithdrawProofOptions = {
  /** Override WASM path/URL (for bundled frontends, use a Vite `?url` import). */
  circuitWasmPath?: string;
  /** Override zkey path/URL (for bundled frontends, use a Vite `?url` import). */
  circuitZkeyPath?: string;
};

export const generateRandomFieldElement = impl.generateRandomFieldElement as () => bigint;

export const createCommitment = impl.createCommitment as (
  nullifier: bigint | number | string,
  secret: bigint | number | string
) => bigint;

export const createNullifierHash = impl.createNullifierHash as (
  nullifier: bigint | number | string
) => bigint;

export const generateDepositParams = impl.generateDepositParams as (
  amountLamports: bigint | number | string,
  mint?: string | null
) => GenerateDepositParamsResult;

export const parseSecretNote = impl.parseSecretNote as (
  note: string
) => Record<string, string>;

export const publicKeyToFieldElement = impl.publicKeyToFieldElement as (
  publicKeyBytes: Uint8Array | number[]
) => bigint;

export const amountToFieldElement = impl.amountToFieldElement as (
  amount: bigint | number | string
) => bigint;

export const generateMerkleProof = impl.generateMerkleProof as (
  depositParams: Record<string, unknown>,
  leaves: unknown[]
) => unknown;

export function generateWithdrawProof(
  depositParams: Record<string, unknown>,
  leaves: unknown[],
  recipient: PublicKey | string,
  relayer: PublicKey | string,
  fee: bigint | number | string,
  refund: bigint | number | string,
  options?: GenerateWithdrawProofOptions
): Promise<unknown> {
  return impl.generateWithdrawProof(
    depositParams,
    leaves,
    recipient,
    relayer,
    fee,
    refund,
    undefined,
    options?.circuitWasmPath,
    options?.circuitZkeyPath
  );
}

export const convertProofToBytes = impl.convertProofToBytes as (
  proof: unknown
) => Promise<Uint8Array | number[]>;

export const to32ByteBuffer = impl.to32ByteBuffer as (
  bigInt: bigint | number | string
) => Uint8Array;

export const toFixedHex = impl.toFixedHex as (
  value: bigint | number | string,
  length?: number
) => string;

export const toFieldElementHex = impl.toFieldElementHex as (
  value: bigint | number | string
) => string;

export const formatNoteForDisplay = impl.formatNoteForDisplay as (
  note: string,
  segmentLen?: number
) => string;
